import styles from './GeofenceForm.module.scss';
import { ModelName } from '@casops/store/api';
import Tabs from '@cas/components/tabs/tabs/Tabs';
import { ServerErrorFields } from '@dmc/ui';
import {
  CreateGeofenceModel,
  EditGeofenceModel,
  GeofenceFormModel,
  GeofenceModel,
  GeofenceRulesModel,
  GeofenceShapeModel,
  useCreateGeofenceMutation,
  useEditGeofenceMutation,
} from '@casops/store/apis/geofencesApi';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import useGeofenceFormSchema from './useGeofenceFormSchema';
import useGeofenceFormTabs from './useGeofenceFormTabs';
import { Dispatch, ReactElement, SetStateAction, useEffect } from 'react';
import useGeofenceShapeField from './useGeofenceShapeField';
import { VdsNotification } from '@volt/volt-extended-components-react';
import { useTranslation } from 'react-i18next';

type DataModel = GeofenceModel;
type FormModel = GeofenceFormModel;
type CreateModel = CreateGeofenceModel;
type EditModel = EditGeofenceModel;

const modelName = ModelName.Geofence;

const defaultShape: GeofenceShapeModel = {
  points: [[]],
  shape: 'Polygon',
  radius: null,
};

const defaultRules: GeofenceRulesModel = {
  areaIds: [],
  createEventOnEntry: false,
  createEventOnExit: false,
  alarmLogicMuteAll: false,
  alarmLogicRunAreaLogic: false,
  createEventIfAtAndDidNotStop: false,
  createEventIfAtAndSpeeding: null,
  speedLimitLoaded: null,
  createEventIfAtForLongerThanSeconds: null,
  createEventIfAtForLessThanSeconds: null,
  driveOutputs: [],
  createEventOnInputs: [],
  interventionEnabled: null,
};

export const defaultGeofenceFormValues: FormModel = {
  name: '',
  desc: '',
  fts: [],
  stid: -1, // Default Style ID
  ags: [],
  wetDuration: null,
  followMeName: null,
  rules: defaultRules,
  shape: defaultShape,
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
  desc: 'Description',
};

interface GeofenceFormProps {
  item?: DataModel;
  editMode?: boolean;
  customSuccessCallback: () => void;
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
}

export function GeofenceForm({
  item,
  editMode,
  customSuccessCallback,
  setIsFormDirty,
}: GeofenceFormProps): ReactElement {
  const { t } = useTranslation();

  const schema = useGeofenceFormSchema();

  const [triggerCreateItem] = useCreateGeofenceMutation();
  const [triggerEditItem] = useEditGeofenceMutation();

  const { canSubmit, onSubmit, errorMessage, control, resetField, isAnyFieldDirty } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues: defaultGeofenceFormValues,
    item,
    schema,
    triggerCreateItem,
    triggerEditItem,
    serverErrorFields,
    validateOnLoad: true,
    editMode,
    customSuccessCallback,
  });

  // Update the parent component with the form's dirty state status.
  useEffect(() => {
    setIsFormDirty(isAnyFieldDirty);
  }, [isAnyFieldDirty, setIsFormDirty]);

  // Handle the state of the 'shape' field with a custom hook.
  const shapeErrorMessage = useGeofenceShapeField({ control });

  const { activeView, ...tabsProps } = useGeofenceFormTabs({ control, resetField });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
      editMode={editMode}
    >
      <>
        <Tabs {...tabsProps} hasBorder />
        <div className={styles.fields}>{activeView}</div>
        {typeof shapeErrorMessage !== 'undefined' && (
          <VdsNotification variant="inline" status="informative" showCloseButton={false}>
            <div slot="header">{t('Validation.SpecifyGeofenceShape')}</div>
            <span slot="body">{shapeErrorMessage}</span>
          </VdsNotification>
        )}
      </>
    </ModelForm>
  );
}

export default GeofenceForm;
